import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { MenuAcciones } from './MenuAcciones';
import estilos from './Tabla.module.css';

export type DireccionOrden = 'asc' | 'desc';

export interface OrdenTabla {
  clave: string;
  direccion: DireccionOrden;
}

/**
 * Papel de la columna cuando la fila se muestra como tarjeta en móvil.
 *
 * `titulo` y `subtitulo` encabezan la tarjeta; `detalle` va en el cuerpo;
 * `accion` se ancla abajo; `oculto` no aparece.
 *
 * **En móvil el cuerpo es opt-in**: sólo se muestran las columnas marcadas
 * como `detalle`. Antes se mostraban todas las que no estuvieran ocultas y una
 * tabla de ocho columnas producía tarjetas ilegibles, que es justo lo que se
 * quería evitar al pasar a tarjetas. Quien quiera el registro completo tiene el
 * botón de ver datos, que lo enseña entero.
 */
export type PapelMovil = 'titulo' | 'subtitulo' | 'detalle' | 'accion' | 'oculto';

export interface Columna<T> {
  clave: string;
  encabezado: string;
  render: (fila: T) => ReactNode;
  ordenable?: boolean;
  alinear?: 'izquierda' | 'derecha' | 'centro';
  ancho?: string;
  movil?: PapelMovil;
}

interface TablaProps<T> {
  datos: readonly T[];
  columnas: readonly Columna<T>[];
  claveFila: (fila: T) => string | number;
  orden?: OrdenTabla | null;
  onOrdenar?: (clave: string) => void;
  onPulsarFila?: (fila: T) => void;
  /** Filas que acaban de llegar o cambiar por tiempo real; se resaltan un instante. */
  clavesResaltadas?: ReadonlySet<string | number>;
  /** Descripción de la tabla para lectores de pantalla. */
  etiqueta: string;
}

function claseAlineacion(alinear?: Columna<unknown>['alinear']): string {
  if (alinear === 'derecha') return estilos.der ?? '';
  if (alinear === 'centro') return estilos.centro ?? '';
  return '';
}

/**
 * Tabla de datos que se adapta al ancho disponible.
 *
 * En escritorio es una tabla normal, ordenable. Por debajo de 860 px cada fila
 * se convierte en una tarjeta legible: obligar al usuario a arrastrar una
 * tabla de 16 columnas en un teléfono no es una interfaz utilizable.
 */
export function Tabla<T>({
  datos,
  columnas,
  claveFila,
  orden,
  onOrdenar,
  onPulsarFila,
  clavesResaltadas,
  etiqueta,
}: TablaProps<T>) {
  const columnaTitulo = columnas.find((c) => c.movil === 'titulo') ?? columnas[0];
  const columnaSubtitulo = columnas.find((c) => c.movil === 'subtitulo');
  const columnasAccion = columnas.filter((c) => c.movil === 'accion');
  const candidatas = columnas.filter(
    (c) =>
      c !== columnaTitulo && c !== columnaSubtitulo && c.movil !== 'accion' && c.movil !== 'oculto',
  );

  const declaradas = candidatas.filter((c) => c.movil === 'detalle');

  /*
   * Si la pantalla no ha declarado ninguna, se enseñan las dos primeras en vez
   * de dejar la tarjeta con sólo el título: es un valor por defecto razonable
   * mientras no se afine esa tabla, y nunca satura.
   */
  const columnasDetalle = declaradas.length > 0 ? declaradas : candidatas.slice(0, 2);

  return (
    <>
      {/* --- Escritorio --- */}
      <div className={estilos.envoltorio}>
        <div className={estilos.scroll}>
          <table className={estilos.tabla}>
            <caption className="sr-only">{etiqueta}</caption>
            <thead>
              <tr>
                {columnas.map((col) => {
                  const activa = orden?.clave === col.clave;
                  const puedeOrdenar = col.ordenable && onOrdenar;

                  return (
                    <th
                      key={col.clave}
                      style={col.ancho ? { width: col.ancho } : undefined}
                      className={[
                        claseAlineacion(col.alinear),
                        puedeOrdenar ? estilos.ordenable : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-sort={
                        activa
                          ? orden.direccion === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                      onClick={puedeOrdenar ? () => onOrdenar(col.clave) : undefined}
                    >
                      {puedeOrdenar ? (
                        <span className={estilos.encabezadoOrden}>
                          {col.encabezado}
                          {activa && (
                            <Icon
                              nombre={orden.direccion === 'asc' ? 'contraer' : 'expandir'}
                              tamano={14}
                              className={estilos.flechaOrden}
                            />
                          )}
                        </span>
                      ) : (
                        col.encabezado
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {datos.map((fila) => {
                const clave = claveFila(fila);
                return (
                  <tr
                    key={clave}
                    className={[
                      onPulsarFila ? estilos.filaPulsable : '',
                      clavesResaltadas?.has(clave) ? estilos.filaResaltada : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={onPulsarFila ? () => onPulsarFila(fila) : undefined}
                  >
                    {columnas.map((col) => (
                      <td key={col.clave} className={claseAlineacion(col.alinear)}>
                        {col.render(fila)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Móvil --- */}
      <div className={estilos.tarjetas} role="list" aria-label={etiqueta}>
        {datos.map((fila) => {
          const clave = claveFila(fila);
          const Contenedor = onPulsarFila ? 'button' : 'div';

          return (
            <Contenedor
              key={clave}
              role="listitem"
              type={onPulsarFila ? 'button' : undefined}
              onClick={onPulsarFila ? () => onPulsarFila(fila) : undefined}
              className={[
                estilos.tarjeta,
                clavesResaltadas?.has(clave) ? estilos.filaResaltada : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className={estilos.tarjetaEncabezado}>
                <div className={estilos.tarjetaTextos}>
                  <div className={estilos.tarjetaTitulo}>{columnaTitulo?.render(fila)}</div>
                  {columnaSubtitulo && (
                    <div className={estilos.tarjetaSubtitulo}>{columnaSubtitulo.render(fila)}</div>
                  )}
                </div>

                {/*
                  Las acciones se recogen en un menú a la derecha, centrado con
                  el bloque de texto. Sueltas ocupaban una fila entera al pie de
                  cada tarjeta: cuatro botones por fila, en una lista de veinte,
                  son ochenta objetivos táctiles compitiendo con los datos.

                  El clic no debe llegar a la tarjeta: abrir el menú no es
                  seleccionar la fila.
                */}
                {columnasAccion.length > 0 && (
                  <div
                    className={estilos.tarjetaMenu}
                    onClick={(evento) => evento.stopPropagation()}
                  >
                    <MenuAcciones etiqueta="Acciones de esta fila">
                      {columnasAccion.map((col) => (
                        <span key={col.clave}>{col.render(fila)}</span>
                      ))}
                    </MenuAcciones>
                  </div>
                )}
              </div>

              {columnasDetalle.length > 0 && (
                <div className={estilos.tarjetaDetalles}>
                  {columnasDetalle.map((col) => (
                    <Fragmento key={col.clave} etiqueta={col.encabezado}>
                      {col.render(fila)}
                    </Fragmento>
                  ))}
                </div>
              )}
            </Contenedor>
          );
        })}
      </div>
    </>
  );
}

function Fragmento({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <>
      <span className={estilos.detalleEtiqueta}>{etiqueta}</span>
      <span className={estilos.detalleValor}>{children}</span>
    </>
  );
}
